import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Container, Row } from "reactstrap";
import {
  asyncJobsSelector,
  asyncJobsInfoSelector,
  asyncJobsFileSelector,
  asyncJobsLoading,
  asyncJobsNoEntriesMessageSelector,
} from "../../selectors";
import NoEntriesMessage from "../../../../components/common/NoEntriesMessage/NoEntriesMessage";
import { isEmpty } from "../../../../utilities/typeChecking";
import { notifyWarning } from "../../../../actions";
import {
  asyncJobTypes,
  asyncJobTypes_coordinator,
  asyncJobTypes_manager,
  exportTypes,
} from "../../constants/asyncJobsConstants";

import {
  setAsyncJobsSearchFilters,
  deleteAsyncJob,
  getAsyncJobsCount,
  getAsyncJobs,
  setAsyncJobsCurrentPage,
  downloadAsyncJobFile,
  clearDownloadedAsyncFile,
  setAsyncJobsItemsPerPage,
} from "../../actions";

import AsyncJobsForm from "./async-jobs-form";
import AsyncJobsTable from "./async-jobs-table";
import "./async-jobs-page.css";
import Pagination from "../../../../components/Pagination/Pagination";
import { PageTitle } from "../../../../components/common";
import FullscreenLoader from "../../../../layouts/FullScreenLoader";
import useRoles from "../../../../components/hooks/useRoles";

const AsyncJobsPage = (e) => {
  const dispatch = useDispatch();
  const asyncJobs = useSelector(asyncJobsSelector);
  const file = useSelector(asyncJobsFileSelector);
  const isLoading = useSelector(asyncJobsLoading);
  const isExport = e.location.pathname.includes("export");
  const { count, itemsPerPage, currentPage } = useSelector(
    asyncJobsInfoSelector
  );
  const noEntriesMessage = useSelector(asyncJobsNoEntriesMessageSelector);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isOpenCollapse, setIsOpenCollapse] = useState(true);
  const { isCoordinator, isManager } = useRoles();
  
  const types = useMemo(() => {
    if (isExport) return exportTypes;

    if (isCoordinator) {
      return asyncJobTypes_coordinator;
    }

    if (isManager) {
      return asyncJobTypes_manager;
    }

    return asyncJobTypes;
  }, [isExport]);

  const toggleCollapse = () => setIsOpenCollapse(!isOpenCollapse);

  useEffect(() => {
    dispatch(setAsyncJobsSearchFilters({ export: isExport }));
  }, []);

  useEffect(() => {
    if (!isEmpty(file) && file.fileBytes.size <= 3) {
      dispatch(notifyWarning("There are no records for export yet"));
    } else if (!isEmpty(file) && file.fileBytes.size > 3) {
      // IE doesn't allow using a blob object directly as link href
      // instead it is necessary to use msSaveOrOpenBlob

      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        // window.navigator.msSaveOrOpenBlob(url);
        window.navigator.msSaveOrOpenBlob(file.fileBytes, file.fileName);
        return;
      }

      const url = URL.createObjectURL(file.fileBytes);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.fileName;
      link.click();
      setTimeout(() => {
        // For Firefox it is necessary to delay revoking the ObjectURL
        window.URL.revokeObjectURL(url);
      }, 100);
      dispatch(clearDownloadedAsyncFile());
    }
  }, [file]);

  const handleSubmit = (data) => {
    dispatch(setAsyncJobsSearchFilters(data));
    dispatch(getAsyncJobsCount(data));
    setIsSubmitted(true);
    toggleCollapse();
  };
  const handleDelete = (id) => {
    dispatch(deleteAsyncJob(id));
  };

  const downloadFile = (id) => {
    dispatch(downloadAsyncJobFile(id));
  };

  useEffect(() => {
    if (count > 0 && isSubmitted) {
      dispatch(getAsyncJobs({ take: itemsPerPage }));
      setIsSubmitted(false);
    }
  }, [count]);

  const onPageChange = (_, page) => {
    dispatch(setAsyncJobsCurrentPage(page + 1));
    const skip = page * itemsPerPage;
    dispatch(getAsyncJobs({ take: itemsPerPage, skip }));
  };

  const onRowsPerPageChange = ({ target }) => {
    dispatch(setAsyncJobsCurrentPage(1));
    dispatch(setAsyncJobsItemsPerPage(Number(target.value)));
    dispatch(getAsyncJobs({ take: Number(target.value), skip: 0 }));
  };

  return (
    <Container
      className="client-profile-entries-page-container async-jobs-main-container col-12 p-0"
      tabIndex="0"
    >
      <PageTitle title={isExport ? "exports" : "mass actions"} />
      <FullscreenLoader loading={isLoading} />

      {asyncJobs.length && isOpenCollapse ? (
        <div className="overlay-collapse-open" onClick={toggleCollapse} />
      ) : null}
      <AsyncJobsForm
        types={types}
        handleSubmit={handleSubmit}
        entries={asyncJobs}
        isOpenCollapse={isOpenCollapse}
        toggleCollapse={toggleCollapse}
        setIsSubmitted={setIsSubmitted}
        isExport={isExport}
      />
      {asyncJobs.length ? null : (
        <div className="async-jobs-page-background-logo" />
      )}

      <AsyncJobsTable
        asyncJobs={asyncJobs}
        handleDelete={handleDelete}
        downloadFile={downloadFile}
        isExport={isExport}
      />

      {count && asyncJobs.length ? (
        <Row className="pagination-row mt-5 mb-5">
          <Pagination
            count={count}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            rowsPerPage={itemsPerPage}
            page={currentPage - 1}
          />
        </Row>
      ) : (
        <NoEntriesMessage payload={noEntriesMessage} />
      )}
    </Container>
  );
};

export default AsyncJobsPage;



// WEBPACK FOOTER //
// ./src/features/organization/pages/async-jobs/async-jobs-page.jsx